import { useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import Actionbar from "src/components/common/actionbar/Authactionbar";
import { Message } from "src/components/common/form/Message";
import useUiuxStore from "src/store/useUiuxStore";
import { useMouseContent } from "src/utils/mouse/mouseContent";
import { HRM3110_R01Content } from "./Content_HRM3110_R01";
import { HRM3110_R01Dto } from "./Dto_HRM3110_R01";
import { HRM3110_R01SearchSection } from "./SearchSection_HRM3110_R01";
import { useHRM3110_R01Store } from "./Store_HRM3110_R01";
import { Table_HRM3110_R01 } from "./Table_HRM3110_R01";

// 좌우 분할 리사이저 설정. 왼쪽(지급) 그리드 너비 비율(%)을 브라우저에 저장한다. 지급 그리드는 컬럼이 2개 더 많아 기본 55%로 둔다.
const RESIZER = {
    code: 'HRM3110_R01',
    type: 'width',
    storageKey: 'HRM3110_R01-split-width',
    min: 5,
    max: 80,
    default: 55,
} as const;

/**
 * 화면의 액션바, 검색 영역, 좌우로 나눈 지급·공제 편집 그리드, 메시지를 조립한다.
 * 등록·삭제 버튼은 마지막으로 클릭한 그리드에 적용된다.
 * 자동 조회는 하지 않으며, 빈 그리드에 입력용 빈 행을 넣는 처리는 Content가 담당한다.
 * @returns HRM3110_R01 화면 JSX
 */
export const Container_HRM3110_R01 = () => {
    // 지급항목 그리드 인스턴스를 가리키는 ref. 편집 확정과 스크롤 이동에 쓴다.
    const allowGridRef = useRef<AgGridReact<HRM3110_R01Dto>>(null);
    // 공제항목 그리드 인스턴스를 가리키는 ref. 편집 확정과 스크롤 이동에 쓴다.
    const deductGridRef = useRef<AgGridReact<HRM3110_R01Dto>>(null);
    // 좌우 분할 영역 전체를 가리키는 ref. 리사이저가 마우스 위치를 너비 비율로 바꿀 때 기준으로 쓴다.
    const containerRef = useRef<HTMLDivElement>(null);
    // 오른쪽(공제) 영역을 가리키는 ref. 공통 리사이저 Hook에 전달한다.
    const infoRef = useRef<HTMLDivElement>(null);
    // true이면 리사이저를 드래그하는 중이다. 공통 리사이저 Hook이 값을 바꾼다.
    const isResizing = useRef(false);
    const { setMessage } = useUiuxStore();
    // 화면 Store에서 등록·삭제 대상 그리드 변경 함수를 가져온다.
    const { setActiveGridType } = useHRM3110_R01Store();
    // 공통 리사이저 Hook에서 왼쪽 그리드 너비 비율과 드래그 시작 함수를 가져온다.
    const { splitValue, handleMouseDown } = useMouseContent({
        resizer: RESIZER,
        isResizing,
        containerRef,
        infoRef,
    });
    const {
        clickable, setClickable, fetchData,
        defaultHRM3110_R01Row, initContainerData, handleInsertClick,
        handleSaveClick, handleDeleteClick, handleExcelClick,
    } = HRM3110_R01Content({ allowGridRef, deductGridRef, setMessage });

    // Content가 제공한 버튼 동작과 그리드 참조를 각 화면 요소에 연결한다.
    return (
        <div className="content-container">
            {/* 공통 액션바: 조회·등록·삭제·저장·Excel·초기화 버튼을 Content 함수에 연결한다. 출력은 연결하지 않아 비활성이다. */}
            <Actionbar
                onFetchClick={fetchData}
                onCreateClick={handleInsertClick}
                onDeleteClick={handleDeleteClick}
                onSaveClick={handleSaveClick}
                onExcelClick={handleExcelClick}
                onInitClick={initContainerData}
            />
            {/* 검색 영역: 구분·과세구분·사용여부 콤보 */}
            <HRM3110_R01SearchSection />
            {/* 좌우 분할 영역: MST3040_R01과 같은 공통 리사이저 배치 */}
            <div className="flex flex-1 p-[12px] gap-[4px] min-h-0" ref={containerRef}>
                {/* 왼쪽 지급항목 영역: 클릭하면 등록·삭제 대상 그리드가 된다. 셀 클릭 등이 이벤트를 멈춰도 먼저 지정되도록 캡처 단계에서 처리한다.
                    mousedown이 아니라 click에서 바꾼다. mousedown에서 Store를 바꾸면 화면이 다시 그려지면서 그리드 컬럼 정의가 새로 만들어지고,
                    행 번호 칸의 + 버튼이 mouseup 전에 다시 그려져 click 이벤트가 사라진다. click은 버튼 동작이 끝난 뒤 다시 그려지므로 영향이 없다. */}
                <div
                    className="h-full"
                    style={{ width: `${splitValue}%` }}
                    onClickCapture={() => setActiveGridType('allow')}
                >
                    {/* 지급 그리드: 통상임금·비과세한도·급여대상·상여대상 컬럼을 포함한다. */}
                    <Table_HRM3110_R01
                        gridType="allow"
                        clickable={clickable}
                        setClickable={setClickable}
                        gridRef={allowGridRef}
                        defaultHRM3110_R01Row={defaultHRM3110_R01Row('allow')}
                        handleInsertClick={handleInsertClick}
                    />
                </div>
                {/* 리사이저: 드래그하면 좌우 그리드 너비 비율을 바꾼다. */}
                <div
                    className="w-[6px] bg-gray-100 cursor-col-resize hover:bg-gray-300"
                    onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => handleMouseDown('LR', e.nativeEvent)}
                    data-comment="리사이저"
                />
                {/* 오른쪽 공제항목 영역: 클릭하면 등록·삭제 대상 그리드가 된다. click에서 바꾸는 이유는 지급항목 영역과 같다. */}
                <div
                    className="h-full"
                    style={{ width: `${100 - splitValue}%` }}
                    ref={infoRef}
                    onClickCapture={() => setActiveGridType('deduct')}
                >
                    {/* 공제 그리드: 같은 급여·상여 여부 컬럼을 급여공제·상여공제로 표시한다. */}
                    <Table_HRM3110_R01
                        gridType="deduct"
                        clickable={clickable}
                        setClickable={setClickable}
                        gridRef={deductGridRef}
                        defaultHRM3110_R01Row={defaultHRM3110_R01Row('deduct')}
                        handleInsertClick={handleInsertClick}
                    />
                </div>
            </div>
            <Message />
        </div>
    );
};
